import {RumGlobal} from '@datadog/browser-rum';
import {useCallback, useEffect} from 'react';

import afterFrame from '../afterFrame';
import {useDatadogRum} from './datadogRum';
import {getDatadogRumViewTimings} from './datadogRumViewTimings';

type Predicate = () => boolean;

type UseFirstTimingHook = () => void;

/**
 * Checks if a timing with the given name has already been triggered in the current view.
 *
 * @param name - The timing name to check
 * @returns True if the timing has been triggered, false otherwise
 */
export function hasTriggeredTiming(name: string) {
  return getDatadogRumViewTimings().has(name);
}

/**
 * The same as `datadogRum.addTiming`, but subsequent calls to the same timing
 * within the same Datadog view will not be recorded.
 *
 * By default, `datadogRum.addTiming` can be called multiple times, and the
 * *last* instance of it is used by Datadog in their metrics. This can be useful
 * sometimes, but usually what we want is to measure the *first* instance of
 * a timing concluding. This lets us record things like "when did the first
 * graph finish loading?" or "how long does it take to load a whole workspace?"
 *
 * @param datadogRum - The Datadog RUM instance
 * @param name - The timing name
 * @param time - Optional timestamp for the timing (defaults to now)
 */
export function addFirstTiming(
  datadogRum: RumGlobal,
  name: string,
  time?: number
): void {
  if (hasTriggeredTiming(name)) {
    return;
  }

  getDatadogRumViewTimings().add(name);
  datadogRum.addTiming(name, time);
}

/**
 * A version of Datadog's addTiming function that only fires once. It returns a
 * function that, when called, will trigger Datadog to track when the timing
 * occurred. It will record only the first time a particular metric is called
 * per-view. So if multiple components trigger the same timing, only the first
 * one will be noted by Datadog RUM.
 *
 * If you want to track when a component mounts on the page for the first time,
 * you can combine `useFirstTiming` and `useLayoutEffect` to achieve that:
 *
 * @example
 * ```js
 * useLayoutEffect(useFirstTiming('my_component_mounted'))
 * ```
 *
 * @param metric - The timing metric name
 * @param logAfterFrame - Whether to also log an after-frame timing (defaults to true)
 * @returns A callback function to trigger the timing
 */
export function useFirstTiming(
  metric: string,
  logAfterFrame = true
): UseFirstTimingHook {
  const datadog = useDatadogRum();

  return useCallback(() => {
    addFirstTiming(datadog, metric);
    if (logAfterFrame) {
      afterFrame(() => {
        addFirstTiming(datadog, `${metric}-afterFrame`);
      });
    }
  }, [datadog, metric, logAfterFrame]);
}

/**
 * Triggers Datadog's addTiming function for the provided metric the first time
 * the predicate function returns true.
 *
 * @example
 * ```js
 * const { data, loading } = useQuery(...)
 * useFirstTimingEventWhen('my_component_loaded', () => !loading)
 * ```
 *
 * @param metric - The timing metric name
 * @param predicate - A function that returns true when the timing should be recorded
 * @param logAfterFrame - Whether to also log an after-frame timing (defaults to true)
 */
export function useFirstTimingEventWhen(
  metric: string,
  predicate: Predicate,
  logAfterFrame = true
): void {
  const datadog = useDatadogRum();

  useEffect(() => {
    if (!hasTriggeredTiming(metric) && predicate()) {
      addFirstTiming(datadog, metric);
      if (logAfterFrame) {
        afterFrame(() => {
          addFirstTiming(datadog, `${metric}-afterFrame`);
        });
      }
    }
  });
}
